// 차트 공통 UI 셸 — 토글 그룹 + 구간비교(브러시) 패널 + ECharts 렌더
import React from 'react';
import ReactECharts from 'echarts-for-react';
import { METRIC_TOGGLES } from './constants';
import { toNumber, formatNumber, formatPercent, formatKrwEokFromManwon, formatFullDateKorean } from './utils';

const METRIC_LABEL_BY_KEY = new Map(METRIC_TOGGLES.map((m) => [m.key, m.label]));

// 측정항목 토글 묶음. exclusive=true 이면 라디오(둘 중 하나만 ON)로 렌더.
// groups(선택): [{ exclusive, keys }] 가 오면 구분선으로 나눠 가로 한 줄로 렌더.
export function MetricToggleGroup({ toggleKeys = [], groups = null, metricVisible, onToggle, exclusive = false, groupName = '' }) {
  const renderCheckbox = (key) => {
    const label = METRIC_LABEL_BY_KEY.get(key);
    if (!label) return null;
    return (
      <label key={key} className="metric-toggle-switch">
        <span className="metric-toggle-label">{label}</span>
        <input
          type="checkbox"
          className="metric-toggle-input"
          checked={metricVisible[key] !== false}
          onChange={(e) => onToggle(key, e.target.checked)}
        />
        <span className="metric-toggle-slider" />
      </label>
    );
  };
  const renderRadio = (key, name) => {
    const label = METRIC_LABEL_BY_KEY.get(key);
    if (!label) return null;
    return (
      <label key={key} className="metric-radio-option">
        <input
          type="radio"
          className="metric-radio-input"
          name={name}
          checked={metricVisible[key] === true}
          onChange={() => onToggle(key, true)}
        />
        <span className="metric-radio-label">{label}</span>
      </label>
    );
  };

  if (Array.isArray(groups) && groups.length > 0) {
    return (
      <div className="ranking-compare-chart-controls">
        <div className="metric-toggles-section metric-toggles-grouped">
          {groups.map((g, gi) => (
            <React.Fragment key={gi}>
              {gi > 0 && <span className="metric-toggle-divider" aria-hidden="true" />}
              <div className={`metric-toggle-group ${g.exclusive ? 'is-radio' : ''}`.trim()}>
                {(g.keys || []).map((key) => (
                  g.exclusive ? renderRadio(key, `metric-radio-${groupName}-${gi}`) : renderCheckbox(key)
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  if (exclusive) {
    return (
      <div className="ranking-compare-chart-controls">
        <div className="metric-toggles-section metric-radios-section">
          {toggleKeys.map((key) => {
            const label = METRIC_LABEL_BY_KEY.get(key);
            if (!label) return null;
            return (
              <label key={key} className="metric-radio-option">
                <input
                  type="radio"
                  className="metric-radio-input"
                  name={`metric-radio-${groupName}`}
                  checked={metricVisible[key] === true}
                  onChange={() => onToggle(key, true)}
                />
                <span className="metric-radio-label">{label}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="ranking-compare-chart-controls">
      <div className="metric-toggles-section">
        {toggleKeys.map((key) => {
          const label = METRIC_LABEL_BY_KEY.get(key);
          if (!label) return null;
          return (
            <label key={key} className="metric-toggle-switch">
              <span className="metric-toggle-label">{label}</span>
              <input
                type="checkbox"
                className="metric-toggle-input"
                checked={metricVisible[key] !== false}
                onChange={(e) => onToggle(key, e.target.checked)}
              />
              <span className="metric-toggle-slider" />
            </label>
          );
        })}
      </div>
    </div>
  );
}

// 브러시(구간) 선택 시 차트 우측에 표시되는 비교 테이블.
// 행 = 항목(매매가/전세가율 …), 열 = 단지 → 단지1 vs 단지2 를 항목별로 한눈에 비교.
export function BrushComparisonPanel({
  panelPrefix = 'ranking-compare-panel',
  title = '구간비교',
  metrics = [],
  brushRangeRows,
  metricVisible,
  onClose,
}) {
  const metricColumns = metrics.filter((metric) => metric?.key && metricVisible[metric.key] !== false);
  if (!brushRangeRows || !Array.isArray(brushRangeRows.items) || brushRangeRows.items.length < 1 || metricColumns.length < 1) {
    return null;
  }

  const formatMetric = (value, formatType) => {
    if (formatType === 'percent') return formatPercent(value);
    if (formatType === 'krw') return formatKrwEokFromManwon(value);
    return formatNumber(value);
  };

  const complexes = brushRangeRows.items;
  const twoWay = complexes.length === 2;
  const fmt = (v, format) => formatMetric(toNumber(v), format || 'number');
  const diffCell = (a, b, format) => {
    const na = toNumber(a);
    const nb = toNumber(b);
    if (na == null || nb == null) return { text: '-', dir: '' };
    const d = na - nb;
    const dir = d > 0 ? 'up' : d < 0 ? 'down' : '';
    const sign = d > 0 ? '+' : d < 0 ? '−' : '';
    return { text: `${sign}${formatMetric(Math.abs(d), format || 'number')}`, dir };
  };
  const phases = [
    { key: 'startRow', cur: false },
    { key: 'endRow', cur: false },
    { key: 'currentRow', cur: true },
  ];
  // 컬럼 헤더에 '시작/종료' 글자 대신 실제 날짜(YY.MM.DD) 표기
  const shortDate = (value) => {
    const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1].slice(2)}.${m[2]}.${m[3]}` : '-';
  };

  return (
    <div className={`ranking-compare-brush-panel ${panelPrefix}-brush-panel`} data-prefix={panelPrefix}>
      <div className="ranking-compare-brush-head">
        <div className="ranking-compare-brush-title">
          {title}
          <div className="ranking-compare-brush-range">
            <span className="start">{formatFullDateKorean(brushRangeRows.startDate)}</span>
            <span className="tilde">~</span>
            <span className="end">{formatFullDateKorean(brushRangeRows.endDate)}</span>
          </div>
        </div>
        <button type="button" className="ranking-compare-brush-close" onClick={onClose} aria-label="닫기">×</button>
      </div>
      <table className={`${panelPrefix}-brush-table ranking-compare-brush-table`}>
        <thead>
          <tr>
            <th className="col-metric-head">항목</th>
            <th className="col-name">단지명</th>
            <th className="col-phase">{shortDate(brushRangeRows.startDate)}</th>
            <th className="col-phase">{shortDate(brushRangeRows.endDate)}</th>
            <th className="col-phase cur">현재</th>
          </tr>
        </thead>
        <tbody>
          {metricColumns.map((metric) => {
            const rowCount = complexes.length + (twoWay ? 1 : 0);
            return (
              <React.Fragment key={`${panelPrefix}-${metric.key}`}>
                {complexes.map((item, idx) => (
                  <tr key={`${panelPrefix}-${metric.key}-cx-${idx}`} className={`complex-row ${idx === 0 ? 'metric-first' : ''}`.trim()}>
                    {idx === 0 && <td className="col-metric" rowSpan={rowCount}>{metric.label}</td>}
                    <td className="col-name" style={{ color: item.color }}>{item.name}</td>
                    {phases.map((ph) => (
                      <td key={ph.key} className={`col-val ${ph.cur ? 'cur' : ''}`.trim()}>{fmt(item?.[ph.key]?.[metric.key], metric.format)}</td>
                    ))}
                  </tr>
                ))}
                {twoWay && (
                  <tr className="diff-row">
                    <td className="col-name">차이</td>
                    {phases.map((ph) => {
                      const d = diffCell(complexes[0]?.[ph.key]?.[metric.key], complexes[1]?.[ph.key]?.[metric.key], metric.format);
                      return <td key={`${ph.key}-diff`} className={`col-diff ${d.dir}`.trim()}>{d.text}</td>;
                    })}
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// 원래 오버레이 표: 차트 바로 위 중앙. 행 = 단지, 열 = 항목(시작/종료/변동률)
export function BrushOverlayTable({
  panelPrefix = 'ranking-compare-panel',
  title = '구간비교',
  metrics = [],
  brushRangeRows,
  onClose,
}) {
  // 토글 상태와 무관하게 구간비교표에는 항목(예: 매매호가·실거래가) 전부 표시
  const metricColumns = metrics.filter((metric) => metric?.key);
  if (!brushRangeRows || !Array.isArray(brushRangeRows.items) || brushRangeRows.items.length < 1 || metricColumns.length < 1) {
    return null;
  }

  const formatMetric = (value, formatType) => {
    if (formatType === 'percent') return formatPercent(value);
    if (formatType === 'krw') return formatKrwEokFromManwon(value);
    return formatNumber(value);
  };

  // 컬럼 헤더에 '시작/종료' 글자 대신 실제 날짜(YY.MM.DD) 표기
  const shortDate = (value) => {
    const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1].slice(2)}.${m[2]}.${m[3]}` : '-';
  };
  const startLabel = shortDate(brushRangeRows.startDate);
  const endLabel = shortDate(brushRangeRows.endDate);

  const tableRows = brushRangeRows.items.map((item) => ({
    ...item,
    cells: metricColumns.map((metric) => {
      const startValue = toNumber(item?.startRow?.[metric.key]);
      const endValue = toNumber(item?.endRow?.[metric.key]);
      // 증감 = 종료 − 시작 (절대 변화). 평균 = 구간 내 데이터 평균. 현재 = 최신 스냅샷
      const deltaValue = (startValue != null && endValue != null) ? endValue - startValue : null;
      const avgValue = toNumber(item?.avgRow?.[metric.key]);
      const currentValue = toNumber(item?.currentRow?.[metric.key]);
      return { metricKey: metric.key, startValue, endValue, deltaValue, avgValue, currentValue, format: metric.format || 'number' };
    }),
  }));

  return (
    <div className={`ranking-compare-brush-overlay ${panelPrefix}-brush-overlay`}>
      <div className="ranking-compare-brush-head">
        <div className="ranking-compare-brush-title">
          {title}
          <div className="ranking-compare-brush-range">
            <span className="start">{formatFullDateKorean(brushRangeRows.startDate)}</span>
            <span className="tilde">~</span>
            <span className="end">{formatFullDateKorean(brushRangeRows.endDate)}</span>
          </div>
        </div>
        <button type="button" className="ranking-compare-brush-close" onClick={onClose} aria-label="닫기">×</button>
      </div>
      <table className="ranking-compare-brush-table ranking-compare-brush-overlay-table">
        <thead>
          <tr>
            <th rowSpan="2" className="col-name">단지</th>
            {metricColumns.map((metric) => (
              <th key={`${panelPrefix}-ov-${metric.key}`} colSpan="5" className="col-group group-divider">{metric.label}</th>
            ))}
          </tr>
          <tr>
            {metricColumns.flatMap((metric) => ([
              <th key={`${panelPrefix}-ov-${metric.key}-s`} className="col-val group-divider">{startLabel}</th>,
              <th key={`${panelPrefix}-ov-${metric.key}-e`} className="col-val">{endLabel}</th>,
              <th key={`${panelPrefix}-ov-${metric.key}-c`} className="col-val">증감</th>,
              <th key={`${panelPrefix}-ov-${metric.key}-a`} className="col-val">평균</th>,
              <th key={`${panelPrefix}-ov-${metric.key}-cur`} className="col-val">현재</th>,
            ]))}
          </tr>
        </thead>
        <tbody>
          {tableRows.map((item, idx) => (
            <tr key={`${panelPrefix}-ov-row-${idx}`}>
              <td className="col-name" style={{ color: item.color, fontWeight: 800 }}>{item.name}</td>
              {item.cells.flatMap((cell) => ([
                <td key={`${cell.metricKey}-s`} className="col-val group-divider">{formatMetric(cell.startValue, cell.format)}</td>,
                <td key={`${cell.metricKey}-e`} className="col-val">{formatMetric(cell.endValue, cell.format)}</td>,
                <td key={`${cell.metricKey}-c`} className={`col-change ${cell.deltaValue > 0 ? 'up' : cell.deltaValue < 0 ? 'down' : ''}`}>
                  {cell.deltaValue != null && cell.deltaValue !== 0
                    ? `${cell.deltaValue > 0 ? '+' : '-'}${formatMetric(Math.abs(cell.deltaValue), cell.format)}`
                    : (cell.deltaValue === 0 ? '0' : '-')}
                </td>,
                <td key={`${cell.metricKey}-a`} className="col-val">{cell.avgValue != null ? formatMetric(cell.avgValue, cell.format) : '-'}</td>,
                <td key={`${cell.metricKey}-cur`} className="col-val">{cell.currentValue != null ? formatMetric(cell.currentValue, cell.format) : '-'}</td>,
              ]))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// 차트 제목 드롭다운 — 밑줄 + 아래방향 화살표로 드롭다운임을 표시(매물수↔실거래건수 전환)
function ChartTitleDropdown({ options = [], activeKey, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const active = options.find((o) => o.key === activeKey) || options[0];
  return (
    <div className="ranking-compare-chart-title-dd" ref={ref}>
      <button type="button" className="ranking-compare-chart-title-dd-btn" onClick={() => setOpen((o) => !o)}>
        <span>{active?.label}</span>
        <span className="dd-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="ranking-compare-chart-title-dd-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`dd-item ${o.key === activeKey ? 'active' : ''}`.trim()}
              onClick={() => { if (o.key !== activeKey) onChange(o.key); setOpen(false); }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// 단일 차트 카드 — 구간비교 패널(overlay) + 토글 + ECharts 를 한 묶음으로 렌더
export default function ChartCard({
  wrapperClassName = 'ranking-compare-chart-section',
  panelPrefix,
  title,
  titleDropdown = null,
  brushTitle,
  brushMetrics,
  brushRangeRows,
  metricVisible,
  onBrushClose,
  toggleKeys,
  toggleGroups = null,
  onToggle,
  exclusiveToggles = false,
  option,
  chartRef,
  height,
  chartEvents,
}) {
  const hasBrushPanel = !!(brushRangeRows && Array.isArray(brushRangeRows.items) && brushRangeRows.items.length > 0);

  // 브러시 패널 등장/소멸로 차트 폭이 바뀌면 ECharts 를 명시적으로 리사이즈.
  // (브러시 전역 커서는 상위(모달)에서 Ctrl 상태에 따라 관리하므로 여기서 다시 무장하지 않는다.
  //  무조건 재무장하면 Ctrl 없이도 브러시가 켜져 왼쪽 드래그 pan 이 깨진다.)
  React.useEffect(() => {
    const inst = chartRef?.current?.getEchartsInstance?.();
    if (!inst || inst.isDisposed?.()) return undefined;
    const id = setTimeout(() => {
      try {
        inst.resize();
      } catch (e) { /* stale */ }
    }, 80);
    return () => clearTimeout(id);
  }, [hasBrushPanel, chartRef]);

  const body = (
    <div className="ranking-compare-chart-main">
      <div className="ranking-compare-chart-head">
        {titleDropdown ? (
          <ChartTitleDropdown {...titleDropdown} />
        ) : title ? (
          <h4 className="ranking-compare-chart-title">{title}</h4>
        ) : null}
        {!titleDropdown && (
          <MetricToggleGroup toggleKeys={toggleKeys} groups={toggleGroups} metricVisible={metricVisible} onToggle={onToggle} exclusive={exclusiveToggles} groupName={panelPrefix} />
        )}
      </div>
      <ReactECharts
        ref={chartRef}
        option={option}
        onEvents={chartEvents}
        style={{ height, width: '100%' }}
        notMerge
      />
      {/* 원래대로: 차트 바로 위(오버레이) 표 */}
      <BrushOverlayTable
        panelPrefix={panelPrefix}
        title={brushTitle}
        metrics={brushMetrics}
        brushRangeRows={brushRangeRows}
        metricVisible={metricVisible}
        onClose={onBrushClose}
      />
    </div>
  );

  if (!wrapperClassName) return body;
  return <div className={wrapperClassName}>{body}</div>;
}
